import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { axiosClient, url } from '@/api';
import { AxiosError } from 'axios';
import { DocumentType, ErrorObject, KycStatus } from '../common.types';
import { DEVICE_TOKEN } from '@/constants/storage';

export interface UserCredentials {
  email: string;
  password: string;
  deviceToken?: string;
  recaptchaV3Token?: string;
  recaptchaV3Action?: string;
  recaptchaV2Token?: string;
}

export interface SignInStep2Payload {
  rememberMe: boolean;
  code: string;
}

export interface SignInStep1Response {
  accessToken?: string;
  isNeedMfa?: boolean;
  emailVerified: boolean;
  isPersonal: boolean;
  message: string;
  mfaType?: MfaType;
  mobile?: string;
  mobileVerified: boolean;
}

export interface VerificationResponse {
  success: boolean;
  message: string;
  verified: boolean;
  accessToken: string;
}

export enum MfaType {
  NONE = 'NONE',
  REGISTRATION = 'REGISTRATION',
  SMS = 'SMS',
  QR = 'QR',
}

export enum UserTypeBE {
  INDIVIDUAL = 'INDIVIDUAL',
  BUSINESS = 'BUSINESS',
}

export interface AuthUserResponse {
  websocketId?: string;
  emailVerified?: boolean;
  mobileVerified?: boolean;
  notificationAllowed?: boolean;
  finalStepPassed?: boolean;
  mfaType?: MfaType;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  picture?: string;
  referralCode?: string;
  email?: string;
  mobile?: string;
  lastPasswordChangeDate?: Date;
  userType?: UserTypeBE;
  entityName?: string;
  createdAt?: Date;
  kycStatus?: KycStatus;
  termsAndConditionsAgreed?: boolean;
  whereDidYouHearAboutUs?: string;
}

export interface AuthLoginUserResponse {
  firstName?: string;
  lastName?: string;
  mobile?: string;
  whereDidYouHearAboutUs?: string;
}

interface ChangePasswordUnauthorizedUserPayload {
  email: string;
  code: string;
  recaptchaV3Token: string;
  recaptchaV3Action: string;
  recaptchaV2Token?: string;
}

interface ChangePasswordPayload {
  smsCode?: string;
  newPassword?: string;
}

export interface IVerifyDocumentRequest {
  type: DocumentType;
  file: File;
}

export interface SignUpUserPayload {
  email: string;
  password: string;
  referralCode?: string;
  userType: UserTypeBE;
  recaptchaV3Token: string;
  recaptchaV3Action: string;
  recaptchaV2Token?: string;
}

interface VerificationPayload {
  verificationCode: string;
  isLogin?: boolean;
}

export interface GenerateQrResponse {
  qrCode: string;
  secret: string;
}

export interface ActivateQrResponse {
  message: string;
  success: boolean;
}

interface useRecallVoiceOTPResponse {
  accessToken: string | null;
  message: string | null;
  success: boolean;
  verified: boolean;
}

export interface VerifyCodeResponse {
  type: 'REGISTRATION' | 'SMS' | 'QR' | 'NONE' | undefined;
}

export function useSignInStep1<TContext>(
  options?: Omit<
    UseMutationOptions<SignInStep1Response, AxiosError<ErrorObject>, UserCredentials, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<SignInStep1Response, AxiosError<ErrorObject>, UserCredentials, TContext> {
  const key = ['Client', 'SignInStep1'];

  const storedDeviceToken = localStorage.getItem(DEVICE_TOKEN);

  return useMutation(
    async (body: UserCredentials) =>
      await axiosClient.post(url.authController.logInStep1, {
        ...body,
        ...(storedDeviceToken && { deviceToken: storedDeviceToken }),
      }),
    {
      ...options,
      mutationKey: key,
    },
  );
}

export function useSignInStep2<TContext>(
  options?: Omit<
    UseMutationOptions<SignInStep1Response, AxiosError<ErrorObject>, SignInStep2Payload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<SignInStep1Response, AxiosError<ErrorObject>, SignInStep2Payload, TContext> {
  const key = ['Client', 'SignInStep2'];

  return useMutation(async (body: SignInStep2Payload) => await axiosClient.post(url.authController.logInStep2, body), {
    ...options,
    mutationKey: key,
  });
}

export function useSignUp<TContext>(
  options?: Omit<
    UseMutationOptions<void, AxiosError<ErrorObject>, SignUpUserPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<void, AxiosError<ErrorObject>, SignUpUserPayload, TContext> {
  const key = ['SignUpBusiness'];

  return useMutation((body: SignUpUserPayload) => axiosClient.post(url.authController.createUser, body), {
    ...options,
    mutationKey: key,
  });
}

export function useInitVoiceOTP<TContext>(
  options?: Omit<
    UseMutationOptions<VerificationResponse, AxiosError<ErrorObject>, string, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<VerificationResponse, AxiosError<ErrorObject>, string, TContext> {
  const key = ['InitVoiceOTP'];

  return useMutation((mobilePhone: string) => axiosClient.post(url.authController.initVoiceOTP, { mobilePhone }), {
    ...options,
    mutationKey: key,
  });
}

export function useRecallVoiceOTP<TContext>(
  options?: Omit<
    UseMutationOptions<useRecallVoiceOTPResponse, AxiosError<ErrorObject>, string, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<useRecallVoiceOTPResponse, AxiosError<ErrorObject>, string, TContext> {
  const key = ['VoiceOtpReCall'];

  return useMutation((mobilePhone: string) => axiosClient.post(url.authController.recallVoiceOTP, { mobilePhone }), {
    ...options,
    mutationKey: key,
  });
}

export function useVerifyVoiceOTP<TContext>(
  options?: Omit<
    UseMutationOptions<VerificationResponse, AxiosError<ErrorObject>, VerificationPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<VerificationResponse, AxiosError<ErrorObject>, VerificationPayload, TContext> {
  const key = ['VerifyVoiceOTP'];

  return useMutation(
    (body: VerificationPayload) =>
      axiosClient.post(url.authController.verifyVoiceOTP, {
        verificationCode: body.verificationCode,
        login: body.isLogin,
      }),
    {
      ...options,
      mutationKey: key,
    },
  );
}

export function useGetUserQuery<TSelectData = AuthUserResponse, TError = AxiosError<ErrorObject>>(
  options?: Omit<UseQueryOptions<AuthUserResponse, TError, TSelectData>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TSelectData, TError> {
  return useQuery<AuthUserResponse, TError, TSelectData>(
    ['Client', 'GetUser'],
    (): Promise<AuthUserResponse> => axiosClient.get(url.authController.getCurrentUser),
    options,
  );
}

export function useGetLoginUserQuery<TSelectData = AuthLoginUserResponse, TError = AxiosError<ErrorObject>>(
  options?: Omit<UseQueryOptions<AuthLoginUserResponse, TError, TSelectData>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TSelectData, TError> {
  return useQuery<AuthLoginUserResponse, TError, TSelectData>(
    ['GetLoginUser'],
    (): Promise<AuthLoginUserResponse> => axiosClient.get(url.authController.getLoginUserData),
    options,
  );
}

export interface VerifyEmailPayload {
  email: string;
  code: string;
  recaptchaV3Token: string;
  recaptchaV3Action: string;
  recaptchaV2Token?: string;
}

export function useVerifyEmailMutation<TContext>(
  options?: Omit<
    UseMutationOptions<void, AxiosError<ErrorObject>, VerifyEmailPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<void, AxiosError<ErrorObject>, VerifyEmailPayload, TContext> {
  const key = ['Client', 'VerifyEmailKey'];
  return useMutation(
    async (payload: VerifyEmailPayload) => await axiosClient.post(url.authController.verifyEmailCode(), payload),
    {
      ...options,
      mutationKey: key,
    },
  );
}

interface ResendVerificationEmailPayload {
  email: string;
  recaptchaV3Token?: string;
  recaptchaV3Action?: string;
  recaptchaV2Token?: string;
}

export function useResendVerificationEmailMutation<TContext>(
  options?: Omit<
    UseMutationOptions<void, AxiosError<ErrorObject>, ResendVerificationEmailPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<void, AxiosError<ErrorObject>, ResendVerificationEmailPayload, TContext> {
  const key = ['Client', 'ResendVerificationEmailCode'];
  return useMutation(
    async (payload: ResendVerificationEmailPayload) =>
      await axiosClient.post(url.authController.resendVerifyEmailCode(), {
        email: payload.email,
        recaptchaV3Token: payload.recaptchaV3Token || '',
        recaptchaV3Action: payload.recaptchaV3Action || '',
        recaptchaV2Token: payload.recaptchaV2Token,
      }),
    {
      ...options,
      mutationKey: key,
    },
  );
}

interface ResendCodeForPasswordResetPayload {
  email: string;
  recaptchaV3Token: string;
  recaptchaV3Action: string;
  recaptchaV2Token?: string;
}

export function useResendCodeForPasswordResetForUnauthorized<TContext>(
  options?: Omit<
    UseMutationOptions<void, AxiosError<ErrorObject>, ResendCodeForPasswordResetPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<void, AxiosError<ErrorObject>, ResendCodeForPasswordResetPayload, TContext> {
  const key = ['Client', 'ResendCodeForPasswordResetForUnauthorized'];

  return useMutation(
    async (body: ResendCodeForPasswordResetPayload) =>
      await axiosClient.post(url.authController.resendResetPasswordVerificationEmailCode(), {
        email: body.email,
        recaptchaV3Token: body.recaptchaV3Token,
        recaptchaV3Action: body.recaptchaV3Action,
        ...(body.recaptchaV2Token ? { recaptchaV2Token: body.recaptchaV2Token } : {}),
      }),
    { ...options, mutationKey: key },
  );
}

export function useVerifyCodeForUnauthorizedUser<TContext>(
  options?: Omit<
    UseMutationOptions<VerifyCodeResponse, AxiosError<ErrorObject>, ChangePasswordUnauthorizedUserPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<VerifyCodeResponse, AxiosError<ErrorObject>, ChangePasswordUnauthorizedUserPayload, TContext> {
  const key = ['Client', 'VerifyCodeForUnauthorizedUser'];
  return useMutation(
    async (body: ChangePasswordUnauthorizedUserPayload) =>
      await axiosClient.post(url.authController.verifyCodeForUnauthorizedUser(), {
        email: body.email,
        code: body.code,
        recaptchaV3Token: body.recaptchaV3Token,
        recaptchaV3Action: body.recaptchaV3Action,
        ...(body.recaptchaV2Token ? { recaptchaV2Token: body.recaptchaV2Token } : {}),
      }),
    { ...options, mutationKey: key },
  );
}

export function useResetPasswordForUnauthorizedUser<TContext>(
  options?: Omit<
    UseMutationOptions<
      void,
      AxiosError<ErrorObject>,
      ChangePasswordUnauthorizedUserPayload & ChangePasswordPayload,
      TContext
    >,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<
  void,
  AxiosError<ErrorObject>,
  ChangePasswordUnauthorizedUserPayload & ChangePasswordPayload,
  TContext
> {
  const key = ['Client', 'ResetPasswordForUnauthorizedUser'];
  return useMutation(
    async (body: ChangePasswordUnauthorizedUserPayload & ChangePasswordPayload) =>
      await axiosClient.patch(url.authController.verifyCodeForUnauthorizedUser(), {
        email: body.email,
        code: body.code,
        smsCode: body.smsCode,
        newPassword: body.newPassword,
        recaptchaV3Token: body.recaptchaV3Token,
        recaptchaV3Action: body.recaptchaV3Action,
        ...(body.recaptchaV2Token ? { recaptchaV2Token: body.recaptchaV2Token } : {}),
      }),
    { ...options, mutationKey: key },
  );
}

export function useChangePasswordForAuthorizedUser<TContext>(
  options?: Omit<
    UseMutationOptions<void, AxiosError<ErrorObject>, ChangePasswordPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<void, AxiosError<ErrorObject>, ChangePasswordPayload, TContext> {
  const key = ['Client', 'ChangePasswordForAuthorizedUser'];

  return useMutation(
    async (body: ChangePasswordPayload) =>
      await axiosClient.post(url.authController.changePasswordForAuthorizedUser, body),
    { ...options, mutationKey: key },
  );
}

export function useSendVerificationCodeForAuthorisedUser<TSelectData = void, TError = AxiosError<ErrorObject>>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['Client', 'SendVerificationCodeForAuthorisedUser'],
    () => axiosClient.get(url.authController.sendVerificationCodeToAuthorisedUser),
    options,
  );
}

export function useVerifyDocument<TContext>(
  options?: Omit<
    UseMutationOptions<void, AxiosError<ErrorObject>, IVerifyDocumentRequest, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<void, AxiosError<ErrorObject>, IVerifyDocumentRequest, TContext> {
  const key = ['VerifyDocument'];

  return useMutation(
    async (opts: IVerifyDocumentRequest) => {
      const formData = new FormData();
      formData.set('file', opts.file as File);
      return await axiosClient.post(url.authController.verifyDocument(opts.type), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    { ...options, mutationKey: key },
  );
}

export function useSetMerchantUrl<TContext>(
  options?: Omit<UseMutationOptions<void, AxiosError<ErrorObject>, string, TContext>, 'mutationKey' | 'mutationFn'>,
): UseMutationResult<void, AxiosError<ErrorObject>, string, TContext> {
  const key = ['SetMerchantUrl'];

  return useMutation(
    async (preferredUrl: string) => {
      return await axiosClient.post(url.authController.setMerchantUrl(preferredUrl));
    },
    { ...options, mutationKey: key },
  );
}

export function useCheckMfa<TSelectData = { type: MfaType }, TError = AxiosError<ErrorObject>>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['Client', 'CheckMfa'],
    () => axiosClient.get(url.authController.checkMfa),
    options,
  );
}

export function useSendMfaSms<TContext>(
  options?: Omit<UseMutationOptions<void, AxiosError<ErrorObject>, void, TContext>, 'mutationKey' | 'mutationFn'>,
): UseMutationResult<void, AxiosError<ErrorObject>, void, TContext> {
  const key = ['SendMfaSms'];

  return useMutation(
    async () => {
      return await axiosClient.post(url.authController.sendMfaSms);
    },
    { ...options, mutationKey: key },
  );
}

export function useGenerateQr<TContext>(
  options?: Omit<
    UseMutationOptions<GenerateQrResponse, AxiosError<ErrorObject>, void, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<GenerateQrResponse, AxiosError<ErrorObject>, void, TContext> {
  const key = ['GenerateQr'];

  return useMutation(
    async () => {
      return await axiosClient.post(url.authController.generateQr);
    },
    { ...options, mutationKey: key },
  );
}

export function useActivateQr<TContext>(
  options?: Omit<
    UseMutationOptions<ActivateQrResponse, AxiosError<ErrorObject>, string, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<ActivateQrResponse, AxiosError<ErrorObject>, string, TContext> {
  const key = ['ActivateQr'];

  return useMutation(
    async (code: string) => {
      return await axiosClient.post(url.authController.activateQr, { code });
    },
    { ...options, mutationKey: key },
  );
}
